/* eslint-disable no-irregular-whitespace */
/* eslint-disable max-lines-per-function */
import React, { type ReactElement, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { overquotaBlockDate } from 'reactApp/appHelpers/featuresHelpers/features/overquotaBlockDate';
import {
    overquotaCommunicationPayPlacement,
    overquotaModalViewerConfig,
    overquotaNewCommunication,
} from 'reactApp/appHelpers/featuresHelpers/features/overquotaCommunications';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { useBuyClickHandlers } from 'reactApp/hooks/useBuyClickHandlers';
import { sendDwhModal } from 'reactApp/modules/dwh/dwh.helpers';
import { getUserOverquotaState } from 'reactApp/modules/profile/profile.selectors';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import Content from 'reactApp/ui/Content/Content';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import styles from 'reactApp/ui/OverquotaModal/OverquotaModal.css';
import { OverquotaBannerCtrl, sendDwhOverquota, sendTechOverqRadar } from 'reactApp/ui/OverquotaModal/OverquotaModal.helpers';
import { sendPaymentGa } from 'reactApp/utils/paymentGa';

import { OverquotaSpaceBlock } from '../OverquotaSpaceBlock';

const ACTION_STOP_FILES_OPEN = 'stop-files-open';

const place = 'overquota_started';
const name = 'overquota_splash_new';

export type OverquotaViewerModalProps = {
    onClose?: () => void;
};

export const OverquotaViewerModal = memo(function OverquotaPopup({ onClose }: OverquotaViewerModalProps): ReactElement | null {
    const dispatch = useDispatch();
    const storage = useSelector(getCurrentStorage);
    const { used, userTotalSpace } = useSelector(UserQuotaSelectors.getUserQuotaState);
    const overquota = useSelector(UserQuotaSelectors.getOverQuota);
    const { trashSize = 0, blockTime = undefined, state } = useSelector(getUserOverquotaState);
    const theFirstStep = overquotaBlockDate && blockTime && state === 'started';
    const theSecondStep = overquotaBlockDate && blockTime && (state === 'blocked' || state === 'unblocked');

    const hasTrash = trashSize > 0;
    const hasClearButton = overquotaNewCommunication && overquotaModalViewerConfig.clearButton;
    const showModal = Boolean(used && userTotalSpace);

    const getModalName = useCallback(() => {
        if (state === 'full_blocked') {
            return 'mnz_overquota_after_94_day';
        }

        return 'overquota_splash_new';
    }, [state]);

    const { showPaywall } = useBuyClickHandlers({ paySource: 'mnz_block_viewer' });

    useEffect(() => {
        if (!showModal) {
            return;
        }

        sendTechOverqRadar({ state });

        sendDwhOverquota({ action: 'show', storage, place, name });

        emitAnalyticEvent(AnalyticEventNames.BLOCK_OVERQUOTA_FILES_VIEW);
        sendPaymentGa({
            action: ACTION_STOP_FILES_OPEN,
            label: 'view',
            place: storage,
        });

        sendDwhModal({ name: getModalName() });
    }, [showModal]);

    const title = useMemo(() => {
        if (overquotaNewCommunication) {
            return 'Смотреть файлы пока нельзя';
        }

        return 'Посмотреть файлы пока нельзя';
    }, []);

    const content = useMemo(() => {
        if (overquotaNewCommunication) {
            return (
                <>
                    Единое пространство Облака и Почты переполнено на <span>{overquota.value}</span> — увеличьте его с подпиской Mail Space
                    {hasClearButton ? ' или удалите лишнее' : ''}, чтобы снова просматривать файлы
                </>
            );
        }

        return (
            <>
                Облако переполнено на <span>{overquota.value}</span> — увеличьте место или удалите лишнее, чтобы снова просматривать файлы
            </>
        );
    }, [overquota.value, hasClearButton]);

    const clearButtonText = useMemo(() => {
        if (overquotaNewCommunication) {
            if (hasTrash) {
                return 'Очистить';
            }

            if (overquotaBlockDate && blockTime) {
                return 'Удалить лишнее';
            }
        }

        if (hasTrash) {
            return 'Перейти в корзину';
        }

        return 'Очистить';
    }, [hasTrash, blockTime, overquotaBlockDate]);

    const buttonTitle = useMemo(() => {
        if (overquotaNewCommunication) {
            return 'Снять ограничение';
        }

        return 'Увеличить место';
    }, []);

    const onPrimaryHandler = useCallback(() => {
        sendDwhOverquota({ action: 'click', storage, control: OverquotaBannerCtrl.add_space, place, name });

        let search = PaymentUTM.overquotaSplashNew;

        search = PaymentUTM.overquotaSlashViewerSpace;
        emitAnalyticEvent(AnalyticEventNames.BLOCK_OVERQUOTA_FILES_CLICK);
        sendPaymentGa({
            action: ACTION_STOP_FILES_OPEN,
            label: 'click',
            place: storage,
        });

        if (theFirstStep) {
            search = PaymentUTM.overquotaPopupBlockFirstStepIncreaseSpace;
        }

        if (theSecondStep) {
            search = PaymentUTM.overquotaPopupBlockSecondStepIncreaseSpace;
        }

        if (state === 'full_blocked') {
            search = PaymentUTM.overquotaFullBlockedNewModal;
        }

        sendDwhModal({ action: 'click', name: getModalName(), button_name: OverquotaBannerCtrl.add_space });

        if (overquotaCommunicationPayPlacement === 'paywall') {
            showPaywall();
        } else {
            dispatch(historyPush({ id: '/promo/quota', search: `?${search}` }));
        }

        onClose?.();

        sendTechOverqRadar({ state, action: 'click', bttnName: 'increase' });
    }, [onClose, storage, dispatch, state]);

    const onSecondaryHandler = useCallback(() => {
        sendDwhOverquota({ action: 'click', storage, control: OverquotaBannerCtrl.clear, place, name });

        let search = '';

        search = PaymentUTM.overquotaSlashViewerCleaner;
        sendPaymentGa({
            action: ACTION_STOP_FILES_OPEN,
            label: 'cleaner',
            place: storage,
        });

        if (theFirstStep) {
            search = PaymentUTM.overquotaPopupBlockFirstStepClearSpace;
        }
        if (theSecondStep) {
            search = PaymentUTM.overquotaPopupBlockSecondStepClearSpace;
        }

        sendDwhModal({ action: 'click', name: getModalName(), button_name: OverquotaBannerCtrl.clear });

        if (hasTrash) {
            dispatch(
                historyPush({
                    id: EStorageType.trashbin,
                    search: `?${search}`,
                })
            );
        } else {
            dispatch(
                historyPush({
                    id: '/promo/quota',
                    search: `?${search}`,
                    hash: 'cleaner',
                })
            );
        }
        onClose?.();

        sendTechOverqRadar({ state, action: 'click', bttnName: 'clear' });
    }, [onClose, storage, dispatch, hasTrash]);

    const onCloseHandler = useCallback(() => {
        sendDwhOverquota({ action: 'click', storage, control: OverquotaBannerCtrl.closed, place, name });

        sendPaymentGa({
            action: ACTION_STOP_FILES_OPEN,
            label: 'close',
            place: storage,
        });

        sendDwhModal({ action: 'close', name: getModalName() });

        onClose?.();
    }, [onClose, storage]);

    if (!showModal) {
        return null;
    }

    return (
        <Dialog
            open
            header={''}
            closeOnDimmerClick={false}
            id="overquota-alert"
            qaId="overquota-alert"
            onCancel={onCloseHandler}
            className={styles.root}
            size="tiny"
        >
            <Content isModal>
                <div className={styles.content}>
                    <div className={styles.imageWrapper}>
                        <OverquotaSpaceBlock />
                    </div>
                    <div className={styles.title}>{title}</div>
                    <div className={styles.text}>{content}</div>
                    <div className={styles.footerWrapper}>
                        <Button
                            className={styles.button}
                            data-name="freeze-renewal"
                            theme="octavius"
                            sizeMode={ButtonSizeMode.small}
                            onClick={onPrimaryHandler}
                            primary={overquotaNewCommunication}
                            error={!overquotaNewCommunication}
                        >
                            {buttonTitle}
                        </Button>
                        {hasClearButton && (
                            <Button
                                className={styles.button}
                                onClick={onSecondaryHandler}
                                theme="octavius"
                                sizeMode={ButtonSizeMode.small}
                                data-name="secondary"
                            >
                                {clearButtonText}
                            </Button>
                        )}
                    </div>
                </div>
            </Content>
        </Dialog>
    );
});

OverquotaViewerModal.displayName = 'OverquotaViewerModal';
